import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, MenuItem, Box, FormControlLabel, Switch
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useTranslation } from 'react-i18next';
import AchievementsService from '../../services/AchievementsService';
import SnackbarUtils from '../../utils/SnackbarUtils';

const CATEGORY_OPTIONS = [
  { value: 'sales', label: 'Ventas' },
  { value: 'tickets', label: 'Tickets' },
  { value: 'clients', label: 'Clientes' },
  { value: 'growth', label: 'Crecimiento' }
];

const ICON_OPTIONS = [
  { value: 1, label: '💰' },
  { value: 2, label: '⭐' },
  { value: 3, label: '🏆' },
  { value: 4, label: '👑' },
  { value: 5, label: '🎫' },
  { value: 6, label: '🎯' },
  { value: 7, label: '🚀' },
  { value: 8, label: '💫' },
  { value: 9, label: '📈' },
  { value: 10, label: '📊' },
  { value: 11, label: '💥' },
  { value: 12, label: '💎' },
  { value: 13, label: '💠' },
  { value: 14, label: '✨' },
  { value: 15, label: '🔥' },
  { value: 16, label: '⚡' },
  { value: 17, label: '🌟' },
  { value: 18, label: '🏅' }
];

const AddEditAchievementModal = ({ open, onClose, onSave, achievement }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'sales',
    icon_type: 1,
    default_requirement_value: '', // Cambiado a default_requirement_value para plantillas
    is_active: true
  });

  useEffect(() => {
    if (open) {
      if (achievement) {
        // Normalizar isActive (camelCase del backend) a is_active (snake_case usado en frontend)
        const isActive = achievement.isActive ?? achievement.is_active ?? true;
        // Normalizar iconType (camelCase del backend) a icon_type (snake_case usado en frontend)
        const iconType = achievement.iconType ?? achievement.icon_type ?? 1;
        // Normalizar defaultRequirementValue
        const requirementValue = achievement.defaultRequirementValue ?? achievement.default_requirement_value ?? achievement.requirementValue ?? achievement.requirement_value ?? achievement.targetValue ?? achievement.target_value ?? '';
        setFormData({
          name: achievement.name || '',
          description: achievement.description || '',
          category: achievement.category || 'sales',
          icon_type: iconType,
          default_requirement_value: requirementValue,
          is_active: isActive
        });
      } else {
        setFormData({
          name: '',
          description: '',
          category: 'sales',
          icon_type: 1,
          default_requirement_value: '',
          is_active: true
        });
      }
    }
  }, [open, achievement]);

  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData({
      ...formData,
      [field]: value
    });
  };

  const handleSave = async () => {
    if (!formData.name) {
      SnackbarUtils.error(t('fill_required_fields') || 'Por favor completa todos los campos requeridos');
      return;
    }

    // El default_requirement_value es opcional (puede ser asignado después)
    let defaultRequirementValue = null;
    if (formData.default_requirement_value) {
      defaultRequirementValue = parseInt(formData.default_requirement_value, 10);
      if (isNaN(defaultRequirementValue) || defaultRequirementValue <= 0) {
        SnackbarUtils.error(t('invalid_requirement_value') || 'El valor requerido por defecto debe ser un número entero mayor a 0');
        return;
      }
    }

    try {
      setLoading(true);
      const achievementData = {
        name: formData.name,
        description: formData.description || '',
        category: formData.category,
        icon_type: parseInt(formData.icon_type, 10),
        default_requirement_value: defaultRequirementValue, // Enviar como default_requirement_value
        is_active: formData.is_active
      };

      console.log('[AddEditAchievementModal] Form Data:', formData);
      console.log('[AddEditAchievementModal] Achievement Data to send:', achievementData);

      if (achievement) {
        console.log('[AddEditAchievementModal] Updating achievement ID:', achievement.id);
        await AchievementsService.updateAchievement(achievement.id, achievementData);
        SnackbarUtils.success(t('achievement_updated') || 'Logro actualizado exitosamente');
      } else {
        console.log('[AddEditAchievementModal] Creating new achievement');
        await AchievementsService.createAchievement(achievementData);
        SnackbarUtils.success(t('achievement_created') || 'Logro creado exitosamente');
      }

      onSave();
    } catch (error) {
      console.error('[AddEditAchievementModal] Error saving achievement:', error);
      console.error('[AddEditAchievementModal] Error response:', error.response?.data);
      SnackbarUtils.error(t('error_saving_achievement') || 'Error al guardar logro');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {achievement ? t('edit_achievement') || 'Editar Logro' : t('add_achievement') || 'Agregar Logro'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label={t('name') || 'Nombre'}
            value={formData.name}
            onChange={handleChange('name')}
            fullWidth
            required
          />
          <TextField
            label={t('description') || 'Descripción'}
            value={formData.description}
            onChange={handleChange('description')}
            fullWidth
            multiline
            rows={3}
          />
          <TextField
            select
            label={t('category') || 'Categoría'}
            value={formData.category}
            onChange={handleChange('category')}
            fullWidth
            required
          >
            {CATEGORY_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label={t('icon') || 'Icono'}
            value={formData.icon_type}
            onChange={handleChange('icon_type')}
            fullWidth
            required
          >
            {ICON_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label} (Tipo {option.value})
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label={t('default_requirement_value') || 'Valor Sugerido (Opcional)'}
            value={formData.default_requirement_value}
            onChange={handleChange('default_requirement_value')}
            fullWidth
            type="number"
            helperText={t('default_requirement_value_help') || 'Este es un valor sugerido que se pre-llenará automáticamente cuando asignes este logro. Puedes cambiarlo al momento de asignarlo a Global, Sucursal o Personal.'}
            inputProps={{ min: 0, step: 1 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.is_active}
                onChange={handleChange('is_active')}
              />
            }
            label={t('active') || 'Activo'}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('cancel') || 'Cancelar'}</Button>
        <LoadingButton onClick={handleSave} loading={loading} variant="contained">
          {t('save') || 'Guardar'}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default AddEditAchievementModal;

